/**
 * Shared helpers for the per-resource hook wrappers under `api/hooks/<resource>.ts`.
 *
 * Post-T6 (orval rewire) the resource hooks are thin wrappers over the
 * Orval-generated raw fetchers in `@/api/generated`. The `ListQueryParams`
 * / `PaginatedResponse` types still flow through this module because the
 * shared `useEntityData` machinery (`pages/admin/hooks/useEntityData.ts`)
 * is keyed on those two shapes — every admin tab passes its `useListXxx`
 * wrapper into `useEntityData` as a `(params: ListQueryParams) =>
 * UseQueryResult<PaginatedResponse<TRow>>`. Lifting those types into the
 * generated layer would mean rewriting the dispatcher; keeping them here
 * is the low-churn path that survives the Orval pipeline.
 */
import type { PaginatedResponse } from '@cotality/marketplace-contracts/endpoints';

// Re-exported so admin-side consumers (`pages/admin/hooks/useEntityData.ts`)
// can pull both the list params and the paginated envelope from one module.
export type { PaginatedResponse } from '@cotality/marketplace-contracts/endpoints';

// Re-exported so page-level consumers can narrow on `instanceof ApiError`
// (e.g. distinguishing 403 from 500 in UI fallback copy) without importing
// directly from `@/api/mutator` — `scripts/lint-contracts.sh` Guard 3
// forbids mutator imports outside `@/api/`, and this barrel sits inside it.
export { ApiError } from '@/api/mutator';

export interface ListQueryParams {
  page?: number;
  pageSize?: number;
  search?: string;
  sort?: string;
  filters?: Record<string, string | undefined>;
}

/**
 * Flatten the dispatcher-side `ListQueryParams` (page/pageSize/search +
 * a `filters` map) into the per-resource flat-params object the Orval
 * generated fetchers expect. Empty strings drop out so the URL doesn't
 * carry `?search=&status=`.
 */
export function flattenListParams(
  params: ListQueryParams | undefined,
): Record<string, unknown> | undefined {
  if (!params) return undefined;
  const out: Record<string, unknown> = {};
  if (params.page !== undefined) out.page = params.page;
  if (params.pageSize !== undefined) out.pageSize = params.pageSize;
  if (params.search !== undefined && params.search !== '') out.search = params.search;
  if (params.sort !== undefined && params.sort !== '') out.sort = params.sort;
  if (params.filters) {
    for (const [key, value] of Object.entries(params.filters)) {
      if (value !== undefined && value !== '') out[key] = value;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Iteration ceiling for {@link fetchAllPages} — a backstop so a backend bug
 * (e.g. a `total` that never shrinks the page) can't spin an unbounded loop.
 * 50 × 100 = 5000 rows, far above any provably-bounded label set we page over.
 */
const FETCH_ALL_MAX_PAGES = 50;

/**
 * Page through a paginated endpoint and return the full, flattened item list.
 *
 * For **provably-bounded** label sets only (teams, categories, flags) that a
 * page-capped lookup would silently truncate — never for open-ended catalogs
 * (plugins, users, submissions), which must be filtered/scoped server-side.
 * Stops when a page returns fewer than `pageSize` items, or at
 * {@link FETCH_ALL_MAX_PAGES} (the guard).
 */
export async function fetchAllPages<T>(
  fetchPage: (params: { page: number; pageSize: number }) => Promise<PaginatedResponse<T>>,
  pageSize = 100,
): Promise<T[]> {
  return (await fetchAllPagesWithTotal(fetchPage, pageSize)).items;
}

/**
 * {@link fetchAllPages} that also carries the server's `total` through.
 *
 * Same contract, same constraints — bounded label sets only. Use this
 * variant when a caller needs BOTH the complete list and the authoritative
 * count, so the count is the server's `COUNT(*)` rather than an array length
 * that a clamped page walk (or a mid-walk mutation) could understate. The
 * team roster is the motivating case: its `reviewerCount` gates a "no
 * reviewers — PRs can't merge" correctness warning, and a count derived from
 * a truncated walk can suppress that warning entirely.
 *
 * `total` is taken from the LAST page fetched — the freshest read — and
 * `pageSize` reports the accumulated length so the returned envelope stays
 * self-consistent as a single logical page.
 */
export async function fetchAllPagesWithTotal<T>(
  fetchPage: (params: { page: number; pageSize: number }) => Promise<PaginatedResponse<T>>,
  pageSize = 100,
): Promise<PaginatedResponse<T>> {
  const all: T[] = [];
  let total = 0;
  for (let page = 1; page <= FETCH_ALL_MAX_PAGES; page++) {
    const res = await fetchPage({ page, pageSize });
    all.push(...res.items);
    total = res.total;
    if (res.items.length < pageSize) break;
  }
  return { items: all, total, page: 1, pageSize: all.length };
}

/**
 * Branded helper so wrapper files can express "the generated 200 envelope
 * is `{ data, success }` at the TS layer, but the mutator unwraps the
 * envelope so the runtime payload is the inner `data` shape — typed here
 * as `PaginatedResponse<T>`". Wrappers cast through this to keep call-site
 * types honest without touching the generated artefact.
 */
export type AsPaginated<T> = PaginatedResponse<T>;
